<!--
  Runtime configuration.

  Credentials here are **write-only**. `GET /api/settings` never returns a key; what comes back
  is a hint derived from the last four characters of the access key *id*, which is a public
  identifier. That mask is rendered as a mask and is never sent back in a `PUT` — round-tripping
  a masked value as if it were real is how a placeholder ends up stored as a credential.

  The keys themselves live in the service's process memory and are never written to disk. The
  durable answer is a named AWS profile, and the panel says so rather than quietly implying the
  bucket configuration will survive a restart with credentials attached.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import { api } from '../lib/api/client';
  import { asProblem, type ApiProblem } from '../lib/api/problem';
  import type {
  BackupSummary, ConnectionTestResult, EffectiveSettings, SettingsUpdate } from '../lib/api/types';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { app } from '../lib/stores/app.svelte';
  import { auth } from '../lib/stores/auth.svelte';

  let settings = $state<EffectiveSettings | null>(null);
  let problem = $state<ApiProblem | null>(null);
  let busy = $state(false);
  let saved = $state(false);
  let test = $state<ConnectionTestResult | null>(null);

  // Backups. Read on mount for an admin; a non-admin sees only whether they are configured,
  // which `GET /api/settings/status` already reports to every signed-in account.
  let backups = $state<BackupSummary | null>(null);
  let backupProblem = $state<ApiProblem | null>(null);
  // What the run just taken reported about itself. Held separately from `backups`, which is a
  // listing and cannot carry it: a finding lives in the manifest, and the manifest is in a
  // bucket this service's credential deliberately cannot read back. So the POST response is
  // the only moment it is visible to a person, and losing it here loses it entirely.
  let lastRunFindings = $state<string[]>([]);
  let backingUp = $state(false);

  /**
   * Amber past 48 hours.
   *
   * Fly's scheduled machines are "approximately" daily, not a cron expression, so one missed
   * run is jitter and two is a fault. Anything tighter would cry wolf; anything looser would
   * let a fortnight go by unnoticed.
   */
  const staleBackup = $derived.by(() => {
    if (!backups?.latest_utc) return backups !== null;
    const age = Date.now() - new Date(backups.latest_utc).getTime();
    return age > 48 * 60 * 60 * 1000;
  });

  async function loadBackups(): Promise<void> {
    if (!auth.isAdmin) return;
    backupProblem = null;
    try {
      backups = await api.backups();
    } catch (error) {
      backupProblem = asProblem(error);
      backups = null;
    }
  }

  async function takeBackup(): Promise<void> {
    backingUp = true;
    backupProblem = null;
    try {
      const run = await api.runBackup();
      lastRunFindings = run.findings ?? [];
      await loadBackups();
      void app.refreshStatus();
    } catch (error) {
      backupProblem = asProblem(error);
    } finally {
      backingUp = false;
    }
  }

  // Form fields. Credential fields start empty and are cleared after a save — this component
  // never holds a secret longer than the request that carries it.
  let bucket = $state('');
  let keyPrefix = $state('');
  let region = $state('');
  let profile = $state('');
  let ttl = $state(300);
  let accessKeyId = $state('');
  let secretAccessKey = $state('');
  let sessionToken = $state('');

  function hydrate(value: EffectiveSettings): void {
    settings = value;
    bucket = value.s3.bucket ?? '';
    keyPrefix = value.s3.key_prefix ?? '';
    region = value.s3.region ?? '';
    profile = value.s3.profile ?? '';
    ttl = value.s3.presign_ttl_seconds;
  }

  async function load(): Promise<void> {
    busy = true;
    problem = null;
    try {
      hydrate(await api.settings());
    } catch (error) {
      problem = asProblem(error);
    } finally {
      busy = false;
    }
  }

  $effect(() => {
    untrack(() => {
      if (!settings && !busy) void load();
      if (backups === null && !backupProblem) void loadBackups();
    });
  });

  async function save(): Promise<void> {
    busy = true;
    problem = null;
    saved = false;
    const payload: SettingsUpdate = {
      bucket: bucket.trim() || null,
      key_prefix: keyPrefix.trim(),
      region: region.trim() || null,
      profile: profile.trim() || null,
      presign_ttl_seconds: ttl,
      updated_by: annotator.id || null,
    };
    // Only ever send credentials the operator just typed. Never the mask.
    if (accessKeyId && secretAccessKey) {
      payload.access_key_id = accessKeyId;
      payload.secret_access_key = secretAccessKey;
      if (sessionToken) payload.session_token = sessionToken;
    }
    try {
      hydrate(await api.saveSettings(payload));
      accessKeyId = secretAccessKey = sessionToken = '';
      saved = true;
      await app.refreshStatus();
    } catch (error) {
      problem = asProblem(error);
    } finally {
      busy = false;
    }
  }

  async function clearCredentials(): Promise<void> {
    busy = true;
    try {
      hydrate(await api.saveSettings({ clear_credentials: true, updated_by: annotator.id || null }));
    } catch (error) {
      problem = asProblem(error);
    } finally {
      busy = false;
    }
  }

  async function runTest(): Promise<void> {
    busy = true;
    test = null;
    try {
      test = await api.testConnection();
      await app.refreshStatus();
    } catch (error) {
      problem = asProblem(error);
    } finally {
      busy = false;
    }
  }
</script>

<div class="settings col">
  <div class="panel col">
    <h2>Imagery source</h2>
    <p class="small muted">
      The browser loads imagery straight from S3 with short-lived presigned URLs; image bytes
      never pass through the annotation service. Without a bucket there is nothing to annotate.
    </p>

    {#if problem}
      <div class="banner error small">
        <strong>{problem.code}</strong>
        <p>{problem.message}</p>
      </div>
    {/if}
    {#if saved}
      <div class="banner ok small">Saved. The CLI and this service now read the same values.</div>
    {/if}

    <div class="grid">
      <label class="col small">
        Bucket
        <input type="text" bind:value={bucket} placeholder="my-hismaic-imagery" />
      </label>
      <label class="col small">
        Key prefix
        <input type="text" bind:value={keyPrefix} placeholder="www/" />
      </label>
      <label class="col small">
        Region
        <input type="text" bind:value={region} placeholder="us-east-1" />
      </label>
      <label class="col small">
        AWS profile
        <input type="text" bind:value={profile} placeholder="default" />
      </label>
      <label class="col small">
        Presign TTL (seconds)
        <input type="number" min="30" max="3600" bind:value={ttl} />
      </label>
    </div>

    <div class="row wrap">
      <button class="primary" onclick={() => void save()} disabled={busy}>Save</button>
      <button onclick={() => void runTest()} disabled={busy}>Test connection</button>
      <button class="subtle" onclick={() => void load()} disabled={busy}>Reload</button>
    </div>
  </div>

  <div class="panel col">
    <h2>Credentials</h2>
    <p class="small muted">
      Write-only. They are held in the service's memory for this process only, are never
      written to disk, never logged, and never returned by the API. A named AWS profile is the
      durable option: the secret then lives in <span class="mono">~/.aws/credentials</span>,
      which is already backed up and audited separately from this repository.
    </p>

    {#if settings}
      <div class="small">
        <div>
          resolution: <span class="mono">{settings.s3.credentials.source}</span>
        </div>
        <div>
          in memory:
          <span class="mono">
            {settings.s3.credentials.runtime_credentials_present ? 'yes' : 'no'}
          </span>
          {#if settings.s3.credentials.access_key_hint}
            · key id <span class="mono">{settings.s3.credentials.access_key_hint}</span>
            <span class="muted">(a mask — the real value is never returned)</span>
          {/if}
        </div>
        <div class="muted">
          written to disk: {settings.s3.credentials.persisted ? 'yes' : 'no — by design'}
        </div>
      </div>
    {/if}

    <div class="grid">
      <label class="col small">
        Access key id
        <input type="text" bind:value={accessKeyId} autocomplete="off" spellcheck="false" />
      </label>
      <label class="col small">
        Secret access key
        <input type="password" bind:value={secretAccessKey} autocomplete="off" />
      </label>
      <label class="col small">
        Session token (optional)
        <input type="password" bind:value={sessionToken} autocomplete="off" />
      </label>
    </div>
    <div class="row wrap">
      <button onclick={() => void save()} disabled={busy || !accessKeyId || !secretAccessKey}>
        Install credentials
      </button>
      <button class="subtle" onclick={() => void clearCredentials()} disabled={busy}>
        Clear from memory
      </button>
    </div>
  </div>

  {#if test}
    <div class="banner {test.ok ? 'ok' : 'error'} col">
      <strong>{test.ok ? 'Connection ok' : 'Connection failed'}</strong>
      <p class="small">{test.message}</p>
      {#if test.sample_keys.length}
        <ul class="small mono">
          {#each test.sample_keys as key (key)}
            <li>{key}</li>
          {/each}
        </ul>
      {/if}
      <p class="small muted">checked {new Date(test.checked_utc).toLocaleString()}</p>
    </div>
  {/if}

  {#if settings}
    <div class="panel col">
      <h2>Effective configuration</h2>
      <table class="small">
        <tbody>
          <tr><th>origin</th><td class="mono">{settings.s3.origin ?? 'not configured'}</td></tr>
          <tr><th>corpus</th><td class="mono">{settings.corpus_path}</td></tr>
          <tr><th>data dir</th><td class="mono">{settings.data_dir}</td></tr>
          <tr><th>exports</th><td class="mono">{settings.exports_dir}</td></tr>
          <tr><th>database</th><td class="mono">{settings.database_url}</td></tr>
          <tr><th>lock TTL</th><td class="mono">{settings.lock_ttl_seconds}s</td></tr>
          <!--
            The vocabulary hash. It was a permanent pill in the header, beside the identity and
            the imagery warning — a provenance fact rendered as if it were a live signal, on
            every screen, for the whole session. It belongs with the other things you look up
            when you need to know exactly what this instance is running.
          -->
          {#if app.vocab}
            <tr>
              <th>vocabulary</th>
              <td class="mono">
                {app.vocab.count} classes · {app.vocab.vocab_sha256?.slice(0, 8) ?? 'unhashed'}
              </td>
            </tr>
          {/if}
          <tr>
            <th>override store</th>
            <td class="mono">
              {settings.override_store_path}
              {settings.override_store_exists ? '' : ' (not written yet)'}
            </td>
          </tr>
          <tr>
            <th>overridden</th>
            <td class="mono">{settings.overridden_fields.join(', ') || 'nothing'}</td>
          </tr>
        </tbody>
      </table>
      <p class="small muted">
        The database URL has its userinfo masked by the service; nothing on this screen is a
        secret.
      </p>
    </div>
  {/if}

  {#if app.status}
    <div class="panel col">
      <h2>Readiness</h2>
      <div class="small mono">
        inscriptions {app.status.inscriptions} · images {app.status.images} · annotations
        {app.status.annotations} · vocab classes {app.status.vocab_classes}
      </div>
      {#if app.status.blockers.length}
        <ul class="small">
          {#each app.status.blockers as blocker, index (index)}
            <li>{blocker}</li>
          {/each}
        </ul>
      {:else}
        <p class="small">Ready to annotate.</p>
      {/if}
    </div>
  {/if}

  <!--
    Backups.

    A backup system that has silently stopped is the standard failure, so the age of the last
    good copy is stated here rather than left to somebody to go looking for. Amber past 48h
    (`--warn`, the same colour every other warning in the app uses): the schedule is
    *approximately* daily (Fly's wording), so one missed run is not yet a fault and two is.

    An **incomplete** run — members uploaded, manifest missing — is named separately. It is a
    failed upload, which means the newest good copy is older than the run count suggests.
  -->
  <div class="panel col" data-testid="backups">
    <h2>Backups</h2>
    {#if !auth.isAdmin}
      <p class="small muted">
        Backups are an operator function. Your account can see whether they are configured, not
        the bucket itself.
      </p>
      <p class="small">
        {app.status?.backup_configured ? 'Configured.' : 'Not configured.'}
      </p>
    {:else if backupProblem}
      <p class="small warn-text" role="alert">
        <strong>Could not read the backup bucket.</strong>
        {backupProblem.message}
      </p>
      <p class="small muted mono">code: {backupProblem.code}</p>
    {:else if backups === null}
      <p class="small muted">Reading the bucket…</p>
    {:else}
      <p class="small" class:warn-text={staleBackup}>
        {#if backups.latest_age}
          Last backup <strong>{backups.latest_age} ago</strong>
          · {backups.run_count} run{backups.run_count === 1 ? '' : 's'} in the bucket
        {:else}
          <strong>No complete backup in the bucket.</strong>
        {/if}
      </p>
      {#if staleBackup}
        <p class="small warn-text">
          The daily schedule is approximately daily, so one missed run is not yet a fault — two
          is. Check the scheduled machine.
        </p>
      {/if}
      {#if backups.incomplete_runs.length}
        <p class="small warn-text">
          {backups.incomplete_runs.length} incomplete run{backups.incomplete_runs.length === 1
            ? ''
            : 's'} (uploaded without a manifest, so not a backup):
          <span class="mono">{backups.incomplete_runs.join(', ')}</span>
        </p>
      {/if}
      {#if backups.latest_prefix}
        <p class="small muted mono">{backups.latest_prefix}</p>
      {/if}
      {#if lastRunFindings.length}
        <!-- A backup that succeeded and noticed something. Not an error — the run is in the
             bucket — so it is not `backupProblem`, and not muted either: the case this exists
             for is the native archive being discarded because the redaction stopped working,
             which is a near-miss on the one thing this feature prevents. -->
        <div class="small warn-text" data-testid="backup-findings">
          <strong>The backup completed and reported:</strong>
          <ul>
            {#each lastRunFindings as finding (finding)}
              <li>{finding}</li>
            {/each}
          </ul>
        </div>
      {/if}
      <div class="row">
        <button onclick={() => void takeBackup()} disabled={backingUp}>
          {backingUp ? 'Backing up…' : 'Back up now'}
        </button>
        <span class="small muted">
          The daily one runs on a schedule; this is for before something risky.
        </span>
      </div>
    {/if}
  </div>
</div>

<style>
  /* Svelte scopes a style block to its own component, so the `.warn-text` that
     `SaveStatus`, `TaskQueue`, `InscriptionAttributes` and `DirectionWorkspace` each define
     does NOT reach here. Without this rule the class is inert and every warning in this
     panel — the stale-backup line, the unreadable-bucket line, the findings block — renders
     as ordinary body text. Four documents and two comments claimed a colour that one missing
     rule was quietly withholding. Same declaration as the other four, deliberately: one
     class, one meaning across the app. */
  .warn-text {
    color: var(--warn);
  }

  .settings {
    padding: 12px;
    gap: 12px;
    max-width: 900px;
    margin: 0 auto;
    width: 100%;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }

  th {
    white-space: nowrap;
    width: 140px;
  }
</style>
